import { LiveReportList } from '@futrli-sdk/predict';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import {
  deleteReportMutation,
  duplicateReportMutation,
  publishReportMutation,
  DuplicateReportInput
} from '../../../mutations/reportActions';
import {
  addToPerformanceDashboardsMutation,
  removeFromPerformanceDashboardsMutation
} from '../../../mutations/performanceDashboards';
import { liveReportsQuery } from '../../../queries/liveReports';
import { performanceDashboardsQuery } from '../../../queries/performanceDashboards';

export interface ModifyDashboardViewState extends ApiViewState {
  dashboard?: LiveReportList;
  errorAddingToPerformanceArea: Error | null;
  errorDeleting: Error | null;
  errorDuplicating: Error | null;
  errorRemovingFromPerformanceArea: Error | null;
  errorUpdating: Error | null;
  isAddingToPerformanceArea: boolean;
  isDeleting: boolean;
  isDuplicating: boolean;
  isRemovingFromPerformanceArea: boolean;
  isUpdating: boolean;
  newDashboardId?: string;
}

export class ModifyDashboardView extends ApiView {
  private liveReportsObs: ReturnType<typeof liveReportsQuery>;
  private performanceDashboardsObs: ReturnType<typeof performanceDashboardsQuery>;

  private addToPerformanceAreaMutation: ReturnType<typeof addToPerformanceDashboardsMutation>;
  private deleteMutation: ReturnType<typeof deleteReportMutation>;
  private duplicateMutation: ReturnType<typeof duplicateReportMutation>;
  private publishMutation: ReturnType<typeof publishReportMutation>;
  private removeFromPerformanceAreaMutation: ReturnType<typeof removeFromPerformanceDashboardsMutation>;

  public state: ModifyDashboardViewState = {
    ...getDefaultApiViewState(),
    dashboard: undefined,
    errorAddingToPerformanceArea: null,
    errorDeleting: null,
    errorDuplicating: null,
    errorRemovingFromPerformanceArea: null,
    errorUpdating: null,
    isAddingToPerformanceArea: false,
    isDeleting: false,
    isDuplicating: false,
    isRemovingFromPerformanceArea: false,
    isUpdating: false,
    newDashboardId: undefined
  };

  constructor(
    private workspaceId: string,
    private organisationId: string,
    private dashboardId: string,
    private isConsolidation = false
  ) {
    super();

    this.liveReportsObs = this.addQuery(liveReportsQuery(workspaceId, organisationId, isConsolidation));
    this.performanceDashboardsObs = this.addQuery(performanceDashboardsQuery(workspaceId, organisationId));

    this.deleteMutation = this.addMutation(deleteReportMutation(workspaceId, organisationId, isConsolidation));
    this.duplicateMutation = this.addMutation(duplicateReportMutation(workspaceId, organisationId, isConsolidation));
    this.publishMutation = this.addMutation(publishReportMutation(workspaceId, organisationId, isConsolidation));
    this.addToPerformanceAreaMutation = this.addMutation(
      addToPerformanceDashboardsMutation(workspaceId, organisationId)
    );
    this.removeFromPerformanceAreaMutation = this.addMutation(
      removeFromPerformanceDashboardsMutation(workspaceId, organisationId)
    );

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************

  public addToPerformanceArea(reportName: string) {
    return this.addToPerformanceAreaMutation.mutate({
      reportId: this.dashboardId,
      reportName,
      order: this.performanceDashboards.length ? null : 1 // we set the order to 1 if it's the first dashboard to be added, leaving 0th place for customers and supplies page
    });
  }

  public deleteDashboard() {
    return this.deleteMutation.mutate({ reportId: this.dashboardId });
  }

  public duplicateDashboard(data: Omit<DuplicateReportInput, 'reportId'>) {
    return this.duplicateMutation.mutate({ reportId: this.dashboardId, reportName: data.reportName });
  }

  public publishDashboard() {
    return this.publishMutation.mutate({ reportId: this.dashboardId, status: 'live' });
  }

  public removeFromPerformanceArea() {
    if (this.performanceDashboardId) {
      return this.removeFromPerformanceAreaMutation.mutate({ dashboardId: this.performanceDashboardId });
    }
  }

  public resetMutations() {
    this.deleteMutation.reset();
    this.duplicateMutation.reset();
    this.publishMutation.reset();
    this.addToPerformanceAreaMutation.reset();
    this.removeFromPerformanceAreaMutation.reset();
    this.updateState();
  }

  public unpublishDashboard() {
    return this.publishMutation.mutate({ reportId: this.dashboardId, status: 'draft' });
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************

  private get dashboard() {
    const reports = this.getResult(this.liveReportsObs).data ?? [];
    return reports.find(r => r.id === this.dashboardId);
  }

  private get errorAddingToPerformanceArea() {
    return this.addToPerformanceAreaMutation.getCurrentResult().error;
  }

  private get errorDeleting() {
    return this.deleteMutation.getCurrentResult().error;
  }

  private get errorDuplicating() {
    return this.duplicateMutation.getCurrentResult().error;
  }

  private get errorRemovingFromPerformanceArea() {
    return this.removeFromPerformanceAreaMutation.getCurrentResult().error;
  }

  private get errorUpdating() {
    return this.publishMutation.getCurrentResult().error;
  }

  protected getNewState(): ModifyDashboardViewState {
    return {
      ...super.getNewState(),
      dashboard: this.dashboard,
      errorAddingToPerformanceArea: this.errorAddingToPerformanceArea,
      errorDeleting: this.errorDeleting,
      errorDuplicating: this.errorDuplicating,
      errorRemovingFromPerformanceArea: this.errorRemovingFromPerformanceArea,
      errorUpdating: this.errorUpdating,
      isAddingToPerformanceArea: this.isAddingToPerformanceArea,
      isDeleting: this.isDeleting,
      isDuplicating: this.isDuplicating,
      isRemovingFromPerformanceArea: this.isRemovingFromPerformanceArea,
      isUpdating: this.isUpdating,
      newDashboardId: this.newDashboardId
    };
  }

  private get isAddingToPerformanceArea() {
    return this.addToPerformanceAreaMutation.getCurrentResult().status === 'pending';
  }

  private get isDeleting() {
    return this.deleteMutation.getCurrentResult().status === 'pending';
  }

  private get isDuplicating() {
    return this.duplicateMutation.getCurrentResult().status === 'pending';
  }

  private get isRemovingFromPerformanceArea() {
    return this.removeFromPerformanceAreaMutation.getCurrentResult().status === 'pending';
  }

  private get isUpdating() {
    return this.publishMutation.getCurrentResult().status === 'pending';
  }

  private get newDashboardId() {
    return this.duplicateMutation.getCurrentResult().data?.id;
  }

  private get performanceDashboardId() {
    return this.performanceDashboards.find(d => d.report_id === this.dashboardId)?.id;
  }

  private get performanceDashboards() {
    return this.getResult(this.performanceDashboardsObs).data ?? [];
  }
}
